import React from 'react';
import { Loader2 } from 'lucide-react';
import { clientSurfaceCardClass } from '../../utils/clientChromeClasses';

export type WorkspaceLoadingCardProps = {
  title: string;
  description?: string;
  ariaLabel?: string;
  className?: string;
  /** Tighter card for nested shells (e.g. firm company tab). */
  compact?: boolean;
};

/**
 * Branded full-card loading state (dual rings + spinner) — client analyses bootstrap,
 * run history / snapshot fetches, and firm workspace panels.
 */
export function WorkspaceLoadingCard({
  title,
  description,
  ariaLabel,
  className = '',
  compact = false,
}: WorkspaceLoadingCardProps) {
  const label = ariaLabel ?? title;
  const shellClass = compact
    ? 'min-h-[14rem] px-5 py-10 sm:min-h-[16rem]'
    : 'min-h-[min(26rem,calc(100vh-11rem))] px-6 py-16';

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={label}
      className={`${clientSurfaceCardClass} flex flex-col items-center justify-center ${shellClass} ${className}`.trim()}
    >
      <div className="relative flex h-[5.25rem] w-[5.25rem] items-center justify-center">
        <div className="absolute inset-0 rounded-full border-[3px] border-emerald-100/90" aria-hidden />
        <div
          className="absolute inset-0 animate-[spin_0.9s_linear_infinite] rounded-full border-[3px] border-transparent border-t-emerald-600 border-r-emerald-500/45"
          aria-hidden
        />
        <div
          className="absolute inset-[11px] animate-[spin_1.35s_linear_infinite_reverse] rounded-full border-2 border-transparent border-b-teal-600/55 border-l-emerald-400/40"
          aria-hidden
        />
        <div className="relative flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-emerald-50 via-white to-emerald-50/80 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.9)] ring-1 ring-emerald-200/70">
          <Loader2
            className="h-6 w-6 animate-spin text-emerald-700 [animation-duration:0.85s]"
            strokeWidth={2.25}
            aria-hidden
          />
        </div>
      </div>
      <p className="mt-7 text-sm font-medium text-gray-700">{title}</p>
      {description ? (
        <p className="mt-1 max-w-sm text-center text-xs leading-relaxed text-gray-500">{description}</p>
      ) : null}
    </div>
  );
}
